// JWT Utilities for frontend

/** Claim type used by the API in `JwtService.GenerateToken` for `ClaimTypes.NameIdentifier` */
const CLAIM_NAME_IDENTIFIER =
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

function base64UrlDecodeToString(segment: string): string {
  let s = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);
  return atob(s);
}

/**
 * Reads the authenticated user's business UserId from the JWT (matches API `NameIdentifier` claim).
 */
export function getJwtNameIdentifier(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = base64UrlDecodeToString(parts[1]);
    const payload = JSON.parse(json) as Record<string, unknown>;
    const id = payload[CLAIM_NAME_IDENTIFIER] ?? payload.sub;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

export interface DecodedJWT {
  sub: string; // User ID
  email: string;
  role: string;
  tenant_id: string;
  name?: string;
  subscription_expiry?: number; // Unix timestamp
  exp: number; // Token expiry
  iat: number; // Issued at
  jti: string; // JWT ID
}

/**
 * Decode JWT token without verification (frontend only)
 * Note: This is for reading claims only, not for validation
 */
export function decodeJWT(token: string): DecodedJWT | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = JSON.parse(base64UrlDecodeToString(payload)) as Record<string, unknown>;
    
    return {
      sub: (typeof decoded[CLAIM_NAME_IDENTIFIER] === 'string'
        ? (decoded[CLAIM_NAME_IDENTIFIER] as string)
        : (decoded.sub as string)) as string,
      email: (decoded.email ?? decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress']) as string,
      role: (decoded.role ?? decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) as string,
      tenant_id: decoded.tenant_id as string,
      name: decoded.name as string | undefined,
      subscription_expiry: decoded.subscription_expiry as number | undefined,
      exp: decoded.exp as number,
      iat: decoded.iat as number,
      jti: decoded.jti as string,
    };
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token);
  if (!decoded) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Get subscription expiry date from JWT token
 */
export function getSubscriptionExpiry(token: string): Date | null {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.subscription_expiry) {
    return null;
  }

  return new Date(decoded.subscription_expiry * 1000);
}

/**
 * Check if subscription is expired
 */
export function isSubscriptionExpired(token: string): boolean {
  const expiry = getSubscriptionExpiry(token);
  if (!expiry) return false; // If no expiry, assume active

  return expiry < new Date();
}

/**
 * Get days until subscription expiry
 */
export function getDaysUntilExpiry(token: string): number | null {
  const expiry = getSubscriptionExpiry(token);
  if (!expiry) return null;

  const now = new Date();
  const diff = expiry.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Check if subscription is expiring soon (within 7 days)
 */
export function isSubscriptionExpiringSoon(token: string): boolean {
  const days = getDaysUntilExpiry(token);
  if (days === null) return false;

  return days > 0 && days <= 7;
}

